import React, { useState, useEffect } from 'react';
import { X, Printer, Download, Eye } from 'lucide-react';
import Button from '@/components/ui/Button';
import { api } from '@/services/api';

interface ReceiptPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  invoiceId: string;
  invoiceNumber: string;
}

const ReceiptPreviewModal: React.FC<ReceiptPreviewModalProps> = ({
  isOpen,
  onClose,
  invoiceId,
  invoiceNumber,
}) => {
  const [receiptHtml, setReceiptHtml] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [previewMode, setPreviewMode] = useState<'html' | 'text'>('html');

  useEffect(() => {
    if (isOpen && invoiceId) {
      loadReceipt();
    }
  }, [isOpen, invoiceId, previewMode]);

  const loadReceipt = async () => {
    setIsLoading(true);
    try {
      const response = await api.get(`/api/receipts/${invoiceId}`, {
        params: { format: previewMode }
      });
      
      if (previewMode === 'html') {
        setReceiptHtml(response.data);
      } else {
        // For text mode, create a preformatted text display
        setReceiptHtml(`<pre style="font-family: monospace; white-space: pre-wrap; padding: 20px;">${response.data}</pre>`);
      }
    } catch (error) {
      console.error('Failed to load receipt:', error);
      setReceiptHtml('<div class="p-4 text-red-600">Failed to load receipt</div>');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrint = () => {
    if (previewMode === 'html') {
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(receiptHtml);
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => {
          printWindow.print();
        }, 250);
      }
    } else {
      // For text mode, open in new window and print
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(`<pre>${receiptHtml.replace(/<pre[^>]*>|<\/pre>/g, '')}</pre>`);
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => {
          printWindow.print();
        }, 250);
      }
    }
  };

  const handleDownload = () => {
    const blob = new Blob([receiptHtml], { 
      type: previewMode === 'html' ? 'text/html' : 'text/plain' 
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `receipt-${invoiceNumber}.${previewMode === 'html' ? 'html' : 'txt'}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handlePrintThermal = async () => {
    try {
      const response = await api.post(`/api/receipts/${invoiceId}/print`, {
        format: 'text'
      });
      
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(`<pre>${response.data}</pre>`);
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => {
          printWindow.print();
        }, 250);
      }
    } catch (error) {
      console.error('Failed to generate thermal receipt:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <h2 className="text-lg font-semibold text-gray-900">
              Receipt Preview - {invoiceNumber}
            </h2>
            {isLoading && (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
            )}
          </div>
          <div className="flex items-center space-x-2">
            {/* Format Toggle */}
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setPreviewMode('html')}
                className={`px-3 py-1 text-sm rounded-md transition-colors ${
                  previewMode === 'html'
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Eye className="h-4 w-4 inline mr-1" />
                HTML
              </button>
              <button
                onClick={() => setPreviewMode('text')}
                className={`px-3 py-1 text-sm rounded-md transition-colors ${
                  previewMode === 'text'
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <div className="w-4 h-4 inline mr-1" />
                Text
              </button>
            </div>
            
            {/* Action Buttons */}
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Receipt Content */}
        <div className="flex-1 overflow-auto bg-gray-50">
          <div 
            className="min-h-full"
            dangerouslySetInnerHTML={{ __html: receiptHtml }}
          />
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-between p-4 border-t border-gray-200 bg-gray-50">
          <div className="text-sm text-gray-600">
            {previewMode === 'html' ? 'HTML receipt with QR code' : 'Text receipt for thermal printers'}
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDownload}
              className="flex items-center"
            >
              <Download className="h-4 w-4 mr-2" />
              Download
            </Button>
            {previewMode === 'html' ? (
              <Button
                size="sm"
                onClick={handlePrint}
                className="flex items-center"
              >
                <Printer className="h-4 w-4 mr-2" />
                Print
              </Button>
            ) : (
              <Button
                size="sm"
                onClick={handlePrintThermal}
                className="flex items-center"
              >
                <Printer className="h-4 w-4 mr-2" />
                Print Thermal
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReceiptPreviewModal;
